import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../../../../config/axiosConfig';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import { toPersianDigits, formatNumberInputValue, parseNumberInput } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';

function ExpenseForm({ expense, categories = [], onSuccess, onCancel }) {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        category: '',
        amount: '',
        date: '',
        payee: '',
        description: '',
        attachment: null,
        tags: [],
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attachmentPreview, setAttachmentPreview] = useState(null);

    useEffect(() => {
        if (expense) {
            // Edit mode - populate form with existing data
            setFormData({
                category: expense.category || '',
                amount: expense.amount || '',
                date: expense.date || '',
                payee: expense.payee || '',
                description: expense.description || '',
                attachment: null, // Don't pre-populate file input
                tags: expense.tags || [],
            });
            if (expense.attachment) {
                setAttachmentPreview(expense.attachment);
            }
        } else {
            // Create mode - reset form
            setFormData({
                category: '',
                amount: '',
                date: '',
                payee: '',
                description: '',
                attachment: null,
                tags: [],
            });
            setAttachmentPreview(null);
        }
    }, [expense]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] || null;
        handleChange('attachment', file);
        if (file) {
            // Show file name as preview
            setAttachmentPreview(file.name);
        } else {
            setAttachmentPreview(null);
        }
    };

    const validate = () => {
        const newErrors = {};
        
        if (!formData.category) {
            newErrors.category = 'لطفاً دسته‌بندی را انتخاب کنید';
        }
        
        if (!formData.amount || formData.amount <= 0) {
            newErrors.amount = 'لطفاً مبلغ معتبری وارد کنید';
        }
        
        if (!formData.date) {
            newErrors.date = 'لطفاً تاریخ را انتخاب کنید';
        }
        
        if (!formData.payee || formData.payee.trim() === '') {
            newErrors.payee = 'لطفاً نام دریافت‌کننده را وارد کنید';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('category', formData.category);
            formDataToSend.append('amount', formData.amount);
            formDataToSend.append('date', formData.date);
            formDataToSend.append('payee', formData.payee);
            if (formData.description) {
                formDataToSend.append('description', formData.description);
            }
            if (formData.attachment) {
                formDataToSend.append('attachment', formData.attachment);
            }
            if (formData.tags && formData.tags.length > 0) {
                formDataToSend.append('tags', JSON.stringify(formData.tags));
            }

            if (expense) {
                // Update existing expense
                await api.patch(`${API.expenses}${expense.id}/`, formDataToSend, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                // Create new expense
                await api.post(API.expenses, formDataToSend, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving expense:', error);
            if (error.response?.data) {
                // Set field-specific errors if available
                const fieldErrors = {};
                Object.keys(error.response.data).forEach(key => {
                    if (Array.isArray(error.response.data[key])) {
                        fieldErrors[key] = error.response.data[key][0];
                    } else {
                        fieldErrors[key] = error.response.data[key];
                    }
                });
                setErrors(fieldErrors);
            } else {
                setErrors({ submit: 'خطا در ذخیره هزینه. لطفاً دوباره تلاش کنید.' });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '';
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount));
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.category} required>
                        <InputLabel>دسته‌بندی</InputLabel>
                        <Select
                            value={formData.category}
                            label="دسته‌بندی"
                            onChange={(e) => handleChange('category', e.target.value)}
                        >
                            {categories.map((choice) => (
                                <MenuItem key={choice.id} value={choice.id}>
                                    {choice.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.category && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors.category}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="مبلغ (تومان)"
                        type="text"
                        inputMode="numeric"
                        value={formatNumberInputValue(formData.amount)}
                        onChange={(e) => {
                            const parsed = parseNumberInput(e.target.value, false); // false = integer only
                            handleChange('amount', parsed);
                        }}
                        error={!!errors.amount}
                        helperText={errors.amount}
                        required
                        inputProps={{ min: 0 }}
                    />
                    {formData.amount && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formatCurrency(formData.amount)} تومان
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={6}>
                    <PersianDatePicker
                        label="تاریخ"
                        value={formData.date ? new Date(formData.date) : null}
                        onChange={(date) => {
                            if (date && !isNaN(date.getTime())) {
                                handleChange('date', date.toISOString().split('T')[0]);
                            } else {
                                handleChange('date', '');
                            }
                        }}
                        error={!!errors.date}
                        helperText={errors.date}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="دریافت‌کننده"
                        value={formData.payee}
                        onChange={(e) => handleChange('payee', e.target.value)}
                        error={!!errors.payee}
                        helperText={errors.payee}
                        required
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="توضیحات"
                        multiline
                        rows={4}
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        error={!!errors.description}
                        helperText={errors.description}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        type="file"
                        label="پیوست"
                        onChange={handleFileChange}
                        error={!!errors.attachment}
                        helperText={errors.attachment || (attachmentPreview && `فایل انتخاب شده: ${attachmentPreview}`)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ accept: 'image/*,.pdf,.doc,.docx' }}
                    />
                    {expense?.attachment && !formData.attachment && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            فایل فعلی: <a href={expense.attachment} target="_blank" rel="noopener noreferrer">مشاهده</a>
                        </Typography>
                    )}
                </Grid>

                {errors.submit && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error">
                            {errors.submit}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            onClick={onCancel}
                            disabled={isSubmitting}
                            variant="outlined"
                        >
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'در حال ذخیره...' : (expense ? 'ویرایش' : 'ثبت')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ExpenseForm;

